import React, { useRef, useState } from 'react';
import { omit } from 'lodash';
import { Col, Row } from 'react-bootstrap';
import { SecondaryButton } from '@magoosh/lib/button';
import { ModalProvider, useModalContext } from '@magoosh/context/modal_context';
import SubscriptionForm from './full_registration_form';
import { SubscriptionFormProps, SubscriptionPlan } from '../types';
import { formatPrice } from 'utilities/money';
import RegistrationForm from '@magoosh/pages/payment_flows/subscription_form/registration_form';

interface PurchaseablePlanProps {
  guidedStudy: {
    descriptionHtml: string;
    name: string;
    planProps: SubscriptionFormProps;
  }[];
  selfStudy: {
    descriptionHtml: string;
    name: string;
    planProps: SubscriptionFormProps;
  };
}

interface Props extends SubscriptionFormProps {
  purchasablePlanProps: PurchaseablePlanProps;
}

const SubscriptionFormDataCollectionWrapper: React.FC<Props> = (props) => {
  const { purchasablePlanProps } = props;
  const { setModal } = useModalContext();
  const formikRef = useRef();
  // This is passed through to the normal SubscriptionForm, so omit the extra props powering this
  // data tracking component
  const [selectedPlanProps, setSelectedPlanProps] = useState(omit(props, 'purchasablePlanProps'));

  const handlePlanSelected = (planProps) => {
    setSelectedPlanProps(planProps);

    // The actual subscription form has some Formik values that need to be reinitialized with the
    // new selected subscription plan.
    const orderItems = planProps.subscriptionPlans.map((plan) => {
      return {
        purchasable: plan,
        purchasableCount: plan.initialAmount ? plan.initialAmount : 0,
        priceCentsPerItem: plan.priceCents
      };
    });

    const currentFormikRef = formikRef?.current as any;
    currentFormikRef.setFieldValue('orderItems', orderItems);
    currentFormikRef.setFieldValue('couponCode', planProps.discount ? planProps.discount.couponCode : '');
    currentFormikRef.setFieldValue('discount', planProps.discount);
    currentFormikRef.submitForm();
  };

  // Return true to allow purchase to continue, or false to block
  const recordPurchaseIntent = async () => {
    const planForPurchase = selectedPlanProps.subscriptionPlans[0];
    const urlParams = new URLSearchParams(window.location.search);

    // Purposefully using the standard `fetch` rather than the Magoosh utility here. Zapier expects
    // the request to not have a custom Content-Type header set, which our implementation does.
    // Data sent to this webhook will be logged in a Google Sheet and then analyzed to see which
    // daytime configurations students tried to purchase most.
    // Zap = https://zapier.com/app/editor/143896092
    // Google Sheet = https://docs.google.com/spreadsheets/d/18lBDiYvuvys-UjLc0WI_G6HZnmwd4UX1DTv0sNqnEDI/edit?usp=sharing
    await fetch('https://hooks.zapier.com/hooks/catch/3550629/b9q47gn/', {
      method: 'POST',
      body: JSON.stringify({ plan: planForPurchase, trackingId: urlParams.get('tracking_id') })
    });

    // This is our fake plan - don't allow purchase, but show options that are available
    if (
      planForPurchase.planSlug === 'premium-live-classes-schedule-x' ||
      planForPurchase.planSlug === 'premium-live-classes-schedule-x-in'
    ) {
      setModal({
        header: 'This class looks like it is already full.',
        body: (
          <RealPurchaseOptions
            onPlanSelected={handlePlanSelected}
            purchasablePlanProps={purchasablePlanProps}
          />
        )
      });
      return false;
    }

    return true;
  };

  return (
    <RegistrationForm {...selectedPlanProps} beforeSubmit={recordPurchaseIntent} formikRef={formikRef} />
  );
};

interface RealPurchaseOptionsProps {
  onPlanSelected: (SubscriptionFormProps) => void;
  purchasablePlanProps: PurchaseablePlanProps;
}

const RealPurchaseOptions: React.FC<RealPurchaseOptionsProps> = (props) => {
  const { onPlanSelected, purchasablePlanProps } = props;
  const [selectedLiveClassSession, setSelectedLiveClassSession] = useState(
    purchasablePlanProps.guidedStudy[0]
  );
  const [planPropsForSubmit, setPlanPropsForSubmit] = useState(null);

  const handleCompleteOrderClicked = (planProps) => {
    setPlanPropsForSubmit(planProps);
    onPlanSelected(planProps);
  };

  const handleLiveClassSessionSelected = (planProps) => {
    if (!planProps) return; // "See All Days/Time Options" option was selected
    setSelectedLiveClassSession(planProps);
  };

  return (
    <>
      <div className="alert alert-banner">
        Below are classes that still have seats available + our self-study option, both with a 20% off
        discount applied.
      </div>

      <div>
        <div className="u-border-B-s u-padding-V-s">
          <h4>
            <strong>Guided-Study: Live Class Options</strong>
          </h4>
          <Row>
            <Col xs={12} sm={6} className="u-margin-T-s">
              <div dangerouslySetInnerHTML={{ __html: selectedLiveClassSession.descriptionHtml }} />

              <select
                className="form-control bg-gray-lightest u-margin-T-s"
                onChange={(e) =>
                  handleLiveClassSessionSelected(purchasablePlanProps.guidedStudy[e.target.value])
                }>
                <option>See All Days/Time Options</option>
                {purchasablePlanProps.guidedStudy.map((guidedStudyOption, idx) => (
                  <option key={idx} value={idx}>
                    {guidedStudyOption.name}
                  </option>
                ))}
              </select>
            </Col>
            <Col xs={12} sm={6} className="u-margin-T-s">
              <span className="text-underdash">
                <strong>
                  {formatPrice(selectedLiveClassSession.planProps.discount.discountedPrice, 'USD')}
                </strong>{' '}
                <small>with a 20% discount applied</small>
              </span>
              <br />
              <small>
                If paying in installments,{' '}
                {formatPrice(selectedLiveClassSession.planProps.discount.discountedPrice / 4, 'USD')} today
              </small>
              <SecondaryButton
                block
                className="u-margin-T-xs"
                disabled={!!planPropsForSubmit}
                onClick={() => handleCompleteOrderClicked(selectedLiveClassSession.planProps)}
                submitting={planPropsForSubmit === selectedLiveClassSession.planProps}>
                Select and Complete Order
              </SecondaryButton>
            </Col>
          </Row>
        </div>

        <div className="u-border-B-s u-padding-V-s">
          <h4>
            <strong>Self-Study: {purchasablePlanProps.selfStudy.name}</strong>
          </h4>
          <Row>
            <Col xs={12} sm={6} className="u-margin-T-s">
              <div dangerouslySetInnerHTML={{ __html: purchasablePlanProps.selfStudy.descriptionHtml }} />
            </Col>
            <Col xs={12} sm={6} className="u-margin-T-s">
              <span className="text-underdash">
                <strong>
                  {formatPrice(purchasablePlanProps.selfStudy.planProps.discount.discountedPrice, 'USD')}
                </strong>{' '}
                <small>with a 20% discount applied</small>
              </span>
              <br />
              <small>
                If paying in installments,{' '}
                {formatPrice(purchasablePlanProps.selfStudy.planProps.discount.discountedPrice / 4, 'USD')}{' '}
                today
              </small>
              <SecondaryButton
                block
                className="u-margin-T-xs"
                disabled={!!planPropsForSubmit}
                onClick={() => handleCompleteOrderClicked(purchasablePlanProps.selfStudy.planProps)}
                submitting={planPropsForSubmit === purchasablePlanProps.selfStudy.planProps}>
                Select and Complete Order
              </SecondaryButton>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

// Wrap it up so we can use the setModal function in the component above
const SubscriptionFormDataCollectionWrapperWithProvider: React.FC<Props> = (props) => {
  return (
    <ModalProvider>
      <SubscriptionFormDataCollectionWrapper {...props} />
    </ModalProvider>
  );
};

export default SubscriptionFormDataCollectionWrapperWithProvider;
